/**
 * CareerStatsTabs.js — Three-tab Career Statistics card for Auction player cards.
 * Collapsible, with Batting/Bowling/Fielding tabs and role-aware default selection.
 * Used in PublicAuctionView and LiveAuctionConsole.
 */
import { useState } from 'react';

function StatCell({ value, label }) {
  return (
    <div className="bg-white/10 rounded-lg p-2 text-center min-w-0">
      <p className="text-base sm:text-lg font-extrabold leading-tight truncate">{value ?? '-'}</p>
      <p className="text-[9px] sm:text-[10px] text-white/50 uppercase tracking-wider leading-tight mt-0.5">{label}</p>
    </div>
  );
}

function getDefaultTab(playerCategory) {
  if (!playerCategory) return 'batting';
  const cat = playerCategory.toLowerCase();
  if (cat.includes('bowl') || cat === 'bowler') return 'bowling';
  if (cat.includes('field') || cat === 'wicket keeper' || cat === 'keeper' || cat === 'wk') return 'fielding';
  return 'batting';
}

export default function CareerStatsTabs({ stats, playerCategory }) {
  const [activeTab, setActiveTab] = useState(() => getDefaultTab(playerCategory));
  const [collapsed, setCollapsed] = useState(false);

  if (!stats) return null;

  const tabs = [
    { key: 'batting', label: 'Batting', icon: 'ph-bold ph-baseball' },
    { key: 'bowling', label: 'Bowling', icon: 'ph-bold ph-target' },
    { key: 'fielding', label: 'Fielding', icon: 'ph-bold ph-hand-grabbing' },
  ];

  return (
    <div className="mt-5 pt-5 border-t border-white/10" data-testid="career-stats-tabs">
      {/* Section Header + Tabs */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 text-xs text-white/50 font-bold uppercase tracking-wider hover:text-white/70 transition-colors"
          data-testid="career-stats-toggle"
        >
          <i className={`ph-bold ph-caret-${collapsed ? 'right' : 'down'} text-[10px]`}></i>
          Career Statistics
        </button>
        {!collapsed && (
          <div className="flex gap-1">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 transition-all ${
                  activeTab === t.key
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60'
                }`}
                data-testid={`career-tab-${t.key}`}
              >
                <i className={t.icon}></i>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tab Content (collapsible) */}
      {!collapsed && (
        <>
          {activeTab === 'batting' && (
            <div className="space-y-2" data-testid="career-batting-stats">
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.matches} label="Mat" />
                <StatCell value={stats.batting_innings ?? stats.innings} label="Inn" />
                <StatCell value={stats.runs} label="Runs" />
                <StatCell value={stats.highest_score} label="HS" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.batting_avg} label="Avg" />
                <StatCell value={stats.strike_rate} label="SR" />
                <StatCell value={stats.not_outs} label="NO" />
                <StatCell value={stats.fours} label="4s" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.sixes} label="6s" />
                <StatCell value={stats.fifties} label="50s" />
                <StatCell value={stats.thirties} label="30s" />
                <StatCell value={stats.hundreds} label="100s" />
              </div>
            </div>
          )}

          {activeTab === 'bowling' && (
            <div className="space-y-2" data-testid="career-bowling-stats">
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.matches} label="Mat" />
                <StatCell value={stats.bowling_innings} label="Inn" />
                <StatCell value={stats.overs} label="Overs" />
                <StatCell value={stats.wickets} label="Wkts" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.best_bowling} label="Best" />
                <StatCell value={stats.economy} label="Econ" />
                <StatCell value={stats.bowling_avg} label="Avg" />
                <StatCell value={stats.bowling_sr} label="SR" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.maidens} label="Mdns" />
                <StatCell value={stats.bowling_dots} label="Dots" />
                <StatCell value={stats.three_wicket_hauls} label="3W" />
                <StatCell value={stats.five_wicket_hauls} label="5W" />
              </div>
            </div>
          )}

          {activeTab === 'fielding' && (
            <div className="space-y-2" data-testid="career-fielding-stats">
              <div className="grid grid-cols-4 gap-2">
                <StatCell value={stats.matches} label="Mat" />
                <StatCell value={stats.catches} label="Catches" />
                <StatCell value={stats.stumpings} label="Stump" />
                <StatCell value={stats.run_outs} label="Run Outs" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
